import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Grid,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    useTheme,
    Box,
    FormHelperText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { url } from "../api/axios_client";
import axios from "../api/axios_client";
import { useAdmin } from '../hooks/useAdmin';
import { useTranslation } from "react-i18next";


const IndexInfoDialog = ({ open, onClose, index, onSave, readOnly = false }) => {
    console.log("INDEX DATA:", { index })
    const isAdmin = useAdmin();
    const [isEditing, setIsEditing] = useState(false);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const isArabic = currentLanguage === 'ar';

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));


    useEffect(() => {
        const fetchTypes = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${url}/index-types`);
                if (!response.status === 200) {
                    throw new Error('Failed to fetch index types');
                }
                const data = await response.data;
                setTypes(data);
            } catch (err) {
                console.error('Error fetching types:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTypes();
    }, []);

    const validateField = (name, value) => {
        const constraints = {
            name: { required: true, maxLength: 40, message: t('validation.name_required') },
            name_ar: { required: true, maxLength: 40, message: t('validation.name_ar_required') },
            type_id: { required: true, message: t('validation.type_required') },
            provider: { required: true, maxLength: 120, message: t('validation.provider_required') },
            provider_ar: { required: true, maxLength: 120, message: t('validation.provider_ar_required') },
            description: { required: true, maxLength: 1024, message: t('validation.description_required') },
            description_ar: { required: true, maxLength: 1024, message: t('validation.description_ar_required') },
            source: { required: true, maxLength: 256, message: t('validation.source_required') },
            source_ar: { required: true, maxLength: 256, message: t('validation.source_ar_required') },
            citation: { required: true, maxLength: 1024, message: t('validation.citation_required') },
            citation_ar: { required: true, maxLength: 1024, message: t('validation.citation_ar_required') },
            methodology: { required: true, maxLength: 512, message: t('validation.methodology_required') },
            methodology_ar: { required: true, maxLength: 512, message: t('validation.methodology_ar_required') },
        };

        const error = {};

        if (constraints[name]) {
            if (constraints[name].required && !value) {
                error.message = constraints[name].message;
            }
            else if (constraints[name].maxLength && value.length > constraints[name].maxLength) {
                error.message = `Maximum ${constraints[name].maxLength} characters allowed`;
            }
            else if (constraints[name].type === 'number' && isNaN(Number(value))) {
                error.message = 'Must be a valid number';
            }
        }

        return error;
    };

    const extractMinValue = (legends) => {
        const parsedLegends = legends.map(l => JSON.parse(l.replace(/'/g, '"')));

        // Try to find a legend with array value first (first format)
        const arrayLegend = parsedLegends.find(l => Array.isArray(l.value));
        if (arrayLegend) {
            // Get the first legend with array value
            const firstRangeLegend = parsedLegends
                .filter(l => Array.isArray(l.value))
                .shift();
            return Number(firstRangeLegend.value[0]);
        }

        // If no array values found, look for numeric values (second format)
        const numericLegends = parsedLegends
            .filter(l => l.value !== 'nan' && !isNaN(Number(l.value)) && Number(l.value) > -9000);
        if (numericLegends.length > 0) {
            return Math.min(...numericLegends.map(l => Number(l.value)));
        }

        return null;
    };

    const extractMaxValue = (legends) => {
        const parsedLegends = legends.map(l => JSON.parse(l.replace(/'/g, '"')));

        // Try to find a legend with array value first (first format)
        const arrayLegend = parsedLegends.find(l => Array.isArray(l.value));
        if (arrayLegend) {
            // Get the last legend with array value
            const lastRangeLegend = parsedLegends
                .filter(l => Array.isArray(l.value))
                .pop();
            return Number(lastRangeLegend.value[1]);
        }

        // If no array values found, look for numeric values (second format)
        const numericLegends = parsedLegends
            .filter(l => l.value !== 'nan' && !isNaN(Number(l.value)) && Number(l.value) > -9000);
        if (numericLegends.length > 0) {
            return Math.max(...numericLegends.map(l => Number(l.value)));
        }

        return null;
    };

    const extractRangeDivisor = (legends) => {
        const parsedLegends = legends.map(l => JSON.parse(l.replace(/'/g, '"')));

        // Try to find a legend with array value first (first format)
        const arrayLegends = parsedLegends.filter(l => Array.isArray(l.value));
        if (arrayLegends.length >= 2) {
            // Get two consecutive ranges to calculate the divisor
            const firstRange = arrayLegends[0];
            const secondRange = arrayLegends[1];
            return Number(secondRange.value[0]) - Number(firstRange.value[0]);
        }

        // If no array values found, look for numeric values (second format)
        const numericLegends = parsedLegends
            .filter(l => l.value !== 'nan' && !isNaN(Number(l.value)) && Number(l.value) > -9000)
            .map(l => Number(l.value))
            .sort((a, b) => a - b);

        if (numericLegends.length >= 2) {
            // Calculate the smallest difference between consecutive values
            const differences = [];
            for (let i = 1; i < numericLegends.length; i++) {
                differences.push(numericLegends[i] - numericLegends[i - 1]);
            }
            return Math.min(...differences);
        }

        return null;
    };

    // Update legendColors initialization
    const [legendColors, setLegendColors] = useState(
        index?.legends
            ? index.legends.filter(l => l.value !== 'no data').map(l => l.color)
            : []
    );

    const [editedIndex, setEditedIndex] = useState({
        ...index,
        minValue: index?.legends ? extractMinValue(index.legends) : null,
        maxValue: index?.legends ? extractMaxValue(index.legends) : null,
        rangeDivisor: index?.legends ? extractRangeDivisor(index.legends) : null,
    });

    useEffect(() => {
        if (index) {
            setEditedIndex({
                ...index,
                minValue: index.legends ? extractMinValue(index.legends) : null,
                maxValue: index.legends ? extractMaxValue(index.legends) : null,
                rangeDivisor: index.legends ? extractRangeDivisor(index.legends) : null,
            });
            setLegendColors(
                index.legends
                    ? index.legends.filter(l => l.value !== 'no data').map(l => l.color)
                    : []
            );
        }
    }, [index]);

    const handleRangeDivisorChange = (e) => {
        const value = e.target.value;
        const divisor = value === '' ? '' : Number(value);

        setEditedIndex(prev => ({
            ...prev,
            rangeDivisor: divisor
        }));

        if (divisor > 0) {
            updateRangeLegends();
        }
    };

    const handleMinValueChange = (e) => {
        const value = e.target.value;
        const minValue = value === '' ? '' : value;
        setEditedIndex(prev => ({
            ...prev,
            minValue: minValue
        }));
        if (minValue !== '') {
            updateRangeLegends();
        }
    };

    const handleMaxValueChange = (e) => {
        const value = e.target.value;
        const maxValue = value === '' ? '' : value;
        setEditedIndex(prev => ({
            ...prev,
            maxValue: maxValue
        }));
        if (maxValue !== '') {
            updateRangeLegends();
        }
    };

    const updateRangeLegends = () => {
        const { minValue, maxValue, rangeDivisor } = editedIndex;

        if (minValue === '' || maxValue === '' || rangeDivisor === '' ||
            isNaN(minValue) || isNaN(maxValue) || isNaN(rangeDivisor)) {
            return;
        }

        const ranges = [{ value: "no data", color: "##4A4A4A" }];
        ranges.push({ value: `<=${minValue}`, color: legendColors[0] });

        let currentValue = Number(minValue);
        const maxVal = Number(maxValue);
        const step = Number(rangeDivisor);

        while (currentValue < maxVal) {
            const nextValue = Math.min(currentValue + step, maxVal);
            ranges.push({
                value: `>${currentValue}, <${nextValue}`,
                color: legendColors[ranges.length]
            });
            currentValue = nextValue;
        }

        ranges.push({ value: `>${maxVal}`, color: legendColors[ranges.length] });

        setEditedIndex(prev => ({
            ...prev,
            legends: ranges
        }));
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedIndex({
            ...index,
            minValue: extractMinValue(index.legends),
            maxValue: extractMaxValue(index.legends),
            rangeDivisor: extractRangeDivisor(index.legends),
        })
    };

    const handleSave = () => {
        console.log("edited index", editedIndex)
        onSave(editedIndex);
        setIsEditing(false);
    };

    const handleChange = (field) => (event) => {
        const { value } = event.target;

        setEditedIndex(prev => ({
            ...prev,
            [field]: value
        }));

        if (!value || value.toString().trim() === '') {
            const error = validateField(field, value);
            setErrors(prev => ({
                ...prev,
                [field]: error
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleFlagsChange = (event) => {
        const inputValue = event.target.value;
        setEditedIndex(prev => ({
            ...prev,
            flagsInput: inputValue,
            flags: inputValue.trim() ? inputValue.split(',').map(item => {
                const trimmed = item.trim().toLowerCase();
                return trimmed === 'nan' ? 'nan' : Number(trimmed);
            }) : []
        }));
    };

    const isValidFlag = (flag) => {
        if (flag === 'nan') return true;
        return typeof flag === 'number' && !isNaN(flag);
    };

    const renderRangeLegends = () => (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <TextField
                    type="number"
                    label="Min Value"
                    value={editedIndex.minValue}
                    onChange={handleMinValueChange}
                    disabled={!isEditing}
                    style={{ width: '200px' }}
                />
                <TextField
                    type="number"
                    label="Max Value"
                    value={editedIndex.maxValue}
                    onChange={handleMaxValueChange}
                    disabled={!isEditing}
                    style={{ width: '200px' }}
                />
                <TextField
                    type="number"
                    label="Range Divisor"
                    value={editedIndex.rangeDivisor}
                    onChange={handleRangeDivisorChange}
                    disabled={!isEditing}
                    style={{ width: '200px' }}
                    inputProps={{
                        min: 1,
                        step: 1,
                        onKeyDown: (e) => {
                            if (!/[0-9]/.test(e.key) &&
                                e.key !== 'Backspace' &&
                                e.key !== 'Delete' &&
                                e.key !== 'ArrowLeft' &&
                                e.key !== 'ArrowRight' &&
                                e.key !== 'Tab') {
                                e.preventDefault();
                            }
                        }
                    }}
                />
                {isEditing && <Button onClick={updateRangeLegends}>Update Legend Data</Button>}
            </div>
            <Typography variant="subtitle1" gutterBottom>Legend Data</Typography>
            {isEditing && (
                <Typography sx={{ fontStyle: "italic" }} variant="body2" gutterBottom>
                    Click on the color box to change color
                </Typography>
            )}
            <div style={{
                marginTop: 10,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 20,
                alignItems: 'center'
            }}>
                {editedIndex.legends.map((legend, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        backgroundColor: '#f5f5f5',
                        padding: '8px 16px',
                        borderRadius: '4px'
                    }}>
                        <input
                            type="color"
                            value={legend.color}
                            onChange={(e) => handleColorChange(index, e.target.value)}
                            disabled={!isEditing}
                            style={{
                                width: 24,
                                height: 24,
                                padding: 0,
                                border: 'none'
                            }}
                        />
                        <Typography variant="body2">{legend.value}</Typography>
                    </div>
                ))}
            </div>
        </div>
    );


    const renderCategoryLegends = () => {
        return (
            <div>
                {editedIndex.legends.map((legend, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <TextField
                            label={`Category ${index + 1}`}
                            value={legend.value}
                            onChange={(e) => handleCategoryChange(index, e.target.value)}
                            disabled={!isEditing}
                            style={{ width: '200px', marginRight: '10px' }}
                        />
                        <input
                            type="color"
                            value={legend.color}
                            onChange={(e) => handleColorChange(index, e.target.value)}
                            disabled={!isEditing}
                            style={{ width: '50px', height: '35px' }}
                        />
                        {/* {isEditing && (
                            <IconButton onClick={() => handleRemoveCategory(index)} size="small">
                                <DeleteIcon />
                            </IconButton>
                        )} */}
                    </div>
                ))}
                {/* {isEditing && (
                    <Button onClick={handleAddCategory} variant="outlined" size="small">
                        Add Category
                    </Button>
                )} */}
            </div>
        );
    };

    const handleCategoryChange = (index, value) => {
        const newLegends = [...editedIndex.legends];
        newLegends[index] = { ...newLegends[index], value };
        setEditedIndex(prev => ({
            ...prev,
            legends: newLegends
        }));
    };

    const handleColorChange = (index, color) => {
        const newLegends = [...editedIndex.legends];
        newLegends[index] = { ...newLegends[index], color };
        setEditedIndex(prev => ({
            ...prev,
            legends: newLegends
        }));
    };

    const handleAddCategory = () => {
        setEditedIndex(prev => ({
            ...prev,
            legends: [...prev.legends, { value: '', color: '#000000' }]
        }));
    };

    const handleRemoveCategory = (index) => {
        setEditedIndex(prev => ({
            ...prev,
            legends: prev.legends.filter((_, i) => i !== index)
        }));
    };

    const LINKED_FIELDS = [
        "source",
        "citation",
        "methodology",
        "description",
        "terms",
        "provider",
        "المنهجية",
        "المصدر",
        "الاستشهاد",
        "الوصف",
        "الشروط",
        "المزوّد",
    ];

    function linkifyMarkdown(text) {
        const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
        const parts = [];
        let last = 0;
        let m;

        while ((m = linkRegex.exec(text)) !== null) {
            if (m.index > last) parts.push(text.slice(last, m.index));
            parts.push(
                <a
                    key={m.index}
                    href={m[2]}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {m[1]}
                </a>
            );
            last = m.index + m[0].length;
        }
        if (last < text.length) parts.push(text.slice(last));
        return parts;
    }

    function linkifyPlainUrls(nodes) {
        // regex for bare URLs
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return nodes.flatMap((node, idx) => {
            if (typeof node !== "string") return [node];
            const pieces = [];
            let last = 0;
            let m;
            while ((m = urlRegex.exec(node)) !== null) {
                if (m.index > last) pieces.push(...splitWithLineBreaks(node.slice(last, m.index), idx));
                pieces.push(
                    <a
                        key={`u${idx}-${m.index}`}
                        href={m[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {m[0]}
                    </a>
                );
                last = m.index + m[0].length;
            }
            if (last < node.length) pieces.push(...splitWithLineBreaks(node.slice(last), idx));
            return pieces;
        });
    }
    function splitWithLineBreaks(text, idx) {
        const lines = text.split('\n');
        return lines.flatMap((line, i) =>
            i < lines.length - 1 ? [line, <br key={`br-${idx}-${i}`} />] : [line]
        );
    }


    function renderValue(labelEn, labelAr, enValue, arValue) {
        const label = isArabic ? labelAr : labelEn;
        const raw = isArabic && arValue ? arValue : enValue;
        const isLinkField = LINKED_FIELDS.some(
            (f) =>
                labelEn.toLowerCase().includes(f) || labelAr.toLowerCase().includes(f)
        );

        let content = raw ?? "-";
        if (isLinkField && raw) {
            const mdParsed = linkifyMarkdown(raw);
            content = linkifyPlainUrls(mdParsed);
        }

        return (
            <div style={{ width: "100%", marginBottom: 16 }}>
                <Typography variant="subtitle2" color="textSecondary">
                    {label}
                </Typography>
                <Typography component="div">
                    {content ?? "-"}
                </Typography>
            </div>
        );
    }

    const renderMetadataSection = () => (
        <div>
            <Typography variant="h6" gutterBottom>{t('Index.metadata')}</Typography>
            {isEditing ? (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <TextField
                        name="source"
                        label={'source' + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.source}
                        onChange={handleChange('source')}
                        error={!!errors.source}
                        helperText={errors.source?.message || `${editedIndex.source?.length || 0}/256`}
                        inputProps={{ maxLength: 256 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="source_ar"
                        label={t('Index.source_ar') + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.source_ar}
                        onChange={handleChange('source_ar')}
                        error={!!errors.source_ar}
                        helperText={errors.source_ar?.message || `${editedIndex.source_ar?.length || 0}/256`}
                        inputProps={{ maxLength: 256 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="citation"
                        label={'citation' + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.citation}
                        onChange={handleChange('citation')}
                        disabled={!isEditing}
                        error={!!errors.citation}
                        helperText={errors.citation?.message || `${editedIndex.citation?.length || 0}/1024`}
                        inputProps={{ maxLength: 1024 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="citation_ar"
                        label={t('Index.citation_ar') + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.citation_ar}
                        onChange={handleChange('citation_ar')}
                        disabled={!isEditing}
                        error={!!errors.citation_ar}
                        helperText={errors.citation_ar?.message || `${editedIndex.citation_ar?.length || 0}/1024`}
                        inputProps={{ maxLength: 1024 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="methodology"
                        label={'methodology' + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.methodology}
                        onChange={handleChange('methodology')}
                        disabled={!isEditing}
                        error={!!errors.methodology}
                        helperText={errors.methodology?.message || `${editedIndex.methodology?.length || 0}/512`}
                        inputProps={{ maxLength: 512 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="methodology_ar"
                        label={t('Index.methodology_ar') + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.methodology_ar}
                        onChange={handleChange('methodology_ar')}
                        disabled={!isEditing}
                        error={!!errors.methodology_ar}
                        helperText={errors.methodology_ar?.message || `${editedIndex.methodology_ar?.length || 0}/512`}
                        inputProps={{ maxLength: 512 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="description"
                        label={'description' + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.description}
                        onChange={handleChange('description')}
                        disabled={!isEditing}
                        error={!!errors.description}
                        helperText={errors.description?.message || `${editedIndex.description?.length || 0}/1024`}
                        inputProps={{ maxLength: 1024 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="description_ar"
                        label={t('Index.description_ar') + ' *'}
                        style={{ width: '920px' }}
                        value={editedIndex.description_ar}
                        onChange={handleChange('description_ar')}
                        disabled={!isEditing}
                        error={!!errors.description_ar}
                        helperText={errors.description_ar?.message || `${editedIndex.description_ar?.length || 0}/1024`}
                        inputProps={{ maxLength: 1024 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="terms"
                        label={'terms'}
                        style={{ width: '920px' }}
                        value={editedIndex.terms}
                        onChange={handleChange('terms')}
                        disabled={!isEditing}
                        helperText={`${editedIndex.terms?.length || 0}/512`}
                        inputProps={{ maxLength: 512 }}
                        multiline
                        rows={4}
                    />
                    <TextField
                        name="terms_ar"
                        label={t('Index.terms_ar')}
                        style={{ width: '920px' }}
                        value={editedIndex.terms_ar}
                        onChange={handleChange('terms_ar')}
                        disabled={!isEditing}
                        helperText={`${editedIndex.terms_ar?.length || 0}/512`}
                        inputProps={{ maxLength: 512 }}
                        multiline
                        rows={4}
                    />
                </div>

            ) : (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px",
                    justifyContent: "space-between"
                }}>
                    {renderValue(t('Index.available_from'), t('Index.available_from'),
                        index.available_from && index.available_from !== "None" ?
                            new Date(index.available_from).toLocaleDateString('en-CA') : "N/A")}
                    {renderValue(t('Index.available_to'), t('Index.available_to'),
                        editedIndex.available_to && editedIndex.available_to !== "None" ?
                            new Date(index.available_to).toLocaleDateString('en-CA') : "N/A")}
                    {renderValue(t('Index.source'), t('Index.source_ar'),
                        editedIndex.source, editedIndex.source_ar)}
                    {renderValue(t('Index.citation'), t('Index.citation_ar'),
                        editedIndex.citation, editedIndex.citation_ar)}
                    {renderValue(t('Index.methodology'), t('Index.methodology_ar'),
                        editedIndex.methodology, editedIndex.methodology_ar)}
                    {renderValue(t('Index.description'), t('Index.description_ar'),
                        editedIndex.description, editedIndex.description_ar)}
                    {renderValue(t('Index.terms'), t('Index.terms_ar'),
                        editedIndex.terms, editedIndex.terms_ar)}
                </div>
            )}
        </div>
    );

    const renderBasicInfo = () => (
        <div>
            <Typography variant="h6" gutterBottom>{t('Index.basic_information')}</Typography>
            {isEditing ? (
                <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <TextField
                            name="name"
                            label={'name' + ' *'}
                            style={{ width: '300px' }}
                            value={editedIndex.name}
                            onChange={handleChange('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message || `${editedIndex.name?.length || 0}/40`}
                            inputProps={{ maxLength: 40 }}
                        />
                        <FormControl style={{ width: '300px' }} error={!!errors.type_id}>
                            <InputLabel>{t('Index.type')} *</InputLabel>
                            <Select
                                value={editedIndex.type_id}
                                onChange={handleChange('type_id')}
                                disabled={!isEditing || loading}
                            >
                                {loading ? (
                                    <MenuItem disabled>{t('common.loading')}</MenuItem>
                                ) : error ? (
                                    <MenuItem disabled>{t('common.error_loading')}</MenuItem>
                                ) : types && types.length > 0 ? (
                                    types.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {isArabic ? type.name_ar : type.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>{t('common.no_types')}</MenuItem>
                                )}
                            </Select>
                            {errors.type_id && <FormHelperText>{errors.type_id.message}</FormHelperText>}
                            {error && <FormHelperText error>{error}</FormHelperText>}
                        </FormControl>
                        {/* <TextField
                        name="unit"
                        label="Unit *"
                        style={{ width: '300px' }}
                        value={editedIndex.unit}
                        onChange={handleChange('unit')}
                        disabled={!isEditing}
                        error={!!errors.unit}
                        helperText={errors.unit?.message}
                    /> */}
                        <TextField
                            name="provider"
                            label={'provider' + ' *'}
                            style={{ width: '300px' }}
                            value={editedIndex.provider}
                            onChange={handleChange('provider')}
                            disabled={!isEditing}
                            error={!!errors.provider}
                            helperText={errors.provider?.message || `${editedIndex.provider?.length || 0}/120`}
                            inputProps={{ maxLength: 120 }}
                        />
                        <TextField
                            name="name_ar"
                            label={t('Index.name_ar') + ' *'}
                            style={{ width: '300px' }}
                            value={editedIndex.name_ar}
                            onChange={handleChange('name_ar')}
                            error={!!errors.name_ar}
                            helperText={errors.name_ar?.message || `${editedIndex.name_ar?.length || 0}/40`}
                            inputProps={{ maxLength: 40 }}
                        />
                        <TextField
                            name="provider_ar"
                            label={t('Index.provider_ar') + ' *'}
                            style={{ width: '300px' }}
                            value={editedIndex.provider_ar}
                            onChange={handleChange('provider_ar')}
                            disabled={!isEditing}
                            error={!!errors.provider_ar}
                            helperText={errors.provider_ar?.message || `${editedIndex.provider_ar?.length || 0}/120`}
                            inputProps={{ maxLength: 120 }}
                        />
                    </div>
                </>
            ) : (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px",
                    justifyContent: "space-between",
                    minWidth: "180px"
                }}>
                    {renderValue(t('Index.name'), t('Index.name_ar'), editedIndex.name, editedIndex.name_ar)}
                    {renderValue(t('Index.type'), t('Index.type_ar'),
                        types.find(t => t.id === editedIndex.type_id)?.name,
                        types.find(t => t.id === editedIndex.type_id)?.name_ar)}
                    {renderValue(t('Index.unit'), t('Index.unit_ar'), editedIndex.unit, editedIndex.unit_ar)}
                    {renderValue(t('Index.provider'), t('Index.provider_ar'), editedIndex.provider, editedIndex.provider_ar)}
                </div>
            )}
        </div>
    );

    const renderDataProperties = () => (
        <div>
            {!isEditing && <Typography variant="h6" gutterBottom>{t('Index.data_properties')}</Typography>}
            {isEditing ? (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {/* <TextField
                        name="scale"
                        label="Scale *"
                        type="number"
                        style={{ width: '300px' }}
                        value={editedIndex.scale}
                        onChange={handleChange('scale')}
                        error={!!errors.scale}
                        helperText={errors.scale?.message}
                    />
                    <TextField
                        name="offset"
                        label="Offset *"
                        type="number"
                        style={{ width: '300px' }}
                        value={editedIndex.offset}
                        onChange={handleChange('offset')}
                        disabled={!isEditing}
                        error={!!errors.offset}
                        helperText={errors.offset?.message}
                    />
                    <TextField
                        name="nodata"
                        label="No Data Value *"
                        style={{ width: '300px' }}
                        value={editedIndex.nodata}
                        onChange={handleChange('nodata')}
                        disabled={!isEditing}
                        error={!!errors.nodata}
                        helperText="Enter numbers or 'nan' (e.g., 8, 32, -324, or nan)"
                    />
                    <TextField
                        name="flags"
                        label="Flags"
                        style={{ width: '300px' }}
                        value={editedIndex.flagsInput}
                        onChange={handleFlagsChange}
                        disabled={!isEditing}
                        helperText="Enter numbers or 'nan' separated by commas"
                        error={editedIndex.flags?.some(flag => !isValidFlag(flag))}
                    />
                    <TextField
                        name="resolution"
                        label="Pixel Resolution *"
                        style={{ width: '300px' }}
                        value={editedIndex.resolution}
                        onChange={handleChange('resolution')}
                        disabled={!isEditing}
                        error={!!errors.resolution}
                        helperText={errors.resolution?.message || `${editedIndex.resolution?.length || 0}/40`}
                        inputProps={{ maxLength: 40 }}
                    /> */}
                </div>
            ) : (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px",
                    justifyContent: "space-between",
                    minWidth: "180px"
                }}>
                    {renderValue(t('Index.scale'), t('Index.scale'), editedIndex.scale)}
                    {renderValue(t('Index.offset'), t('Index.offset'), editedIndex.offset)}
                    {renderValue(t('Index.nodata'), t('Index.nodata'), editedIndex.nodata)}
                    {renderValue(t('Index.flags'), t('Index.flags'), editedIndex.flags?.join(', '))}
                    {renderValue(t('Index.temporal_resolution'), t('Index.temporal_resolution_ar'),
                        editedIndex.temporal_resolution, editedIndex.temporal_resolution_ar)}
                    {renderValue(t('Index.resolution'), t('Index.resolution'), editedIndex.resolution)}
                </div>
            )}
        </div>
    );

    const renderLegendSection = () => (
        <div>
            <Typography variant="h6" gutterBottom>Legend Configuration</Typography>
            {isEditing ? (
                <>
                    <FormControl style={{ width: '300px', marginBottom: '20px' }}>
                        <InputLabel>Legend Type *</InputLabel>
                        <Select
                            name="legendType"
                            value={editedIndex.legendType}
                            onChange={handleChange('legendType')}
                        >
                            <MenuItem value="ranges">Ranges</MenuItem>
                            <MenuItem value="category">Category</MenuItem>
                        </Select>
                    </FormControl>
                    {editedIndex.legendType === 'ranges' ? renderRangeLegends() : renderCategoryLegends()}
                </>
            ) : (
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    {renderValue('Legend Type', editedIndex.legendType)}
                    <div style={{ width: '100%' }}>
                        {editedIndex.legends.map((legend, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                marginBottom: '8px'
                            }}>
                                <div style={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: legend.color,
                                    border: '1px solid #ddd'
                                }} />
                                <Typography>{legend.value}</Typography>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    if (!index) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullScreen={fullScreen} sx={{ width: "100vw", sm: "auto" }} fullWidth>
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {isArabic ? index.name_ar : index.name} {t('Index.details')}
                    {!readOnly && isAdmin && (
                        <Tooltip title={isEditing ? t('Index.save') : t('Index.edit')}>
                            <IconButton onClick={isEditing ? handleSave : handleEdit}>
                                {isEditing ? <SaveIcon /> : <EditIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </DialogTitle>
            <DialogContent dividers="true">
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: isEditing ? "column" : "row" },
                        gap: 2,
                        p: 3,
                        maxWidth: 1200,
                        mx: "auto",
                    }}
                >
                    {renderBasicInfo()}
                    {renderDataProperties()}
                    {/* {renderLegendSection()} */}
                    {renderMetadataSection()}
                </Box>
            </DialogContent>
            <DialogActions>
                {!isEditing && <Button onClick={onClose}>{t('Index.close')}</Button>}
                {isEditing && <Button onClick={handleCancel}>{t('Index.cancel')}</Button>}
                {isEditing && <Button onClick={handleSave}>{t('Index.save')}</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default IndexInfoDialog;